import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getHealingRequests, createHealingRequest, submitSessionFeedback, getNotifications, markNotificationsRead } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';
import PatientActivityCheckin from '../components/PatientActivityCheckin';
 
const PatientDashboard = () => {
  const { user, logout } = useAuth();
  const { isMobile } = useWindowSize();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [activitiesData, setActivitiesData] = useState({}); // { requestId: [activities] }
  const [showForm, setShowForm] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [feedbackForm, setFeedbackForm] = useState({ requestId: null, sessionIndex: null, text: '' });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [form, setForm] = useState({
    condition: '',
    description: '',
    urgency: 'normal',
    isForSelf: true,
    beneficiaryName: '',
    beneficiaryAge: '',
    beneficiaryRelationship: 'spouse',
    beneficiaryRelationshipOther: '',
    medicalReportsShared: false,
    medicalReportsNote: '',
    consentComplementary: false,
    consentLiability: false,
    consentTestimonial: false
  });
 
  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(function() {
    var fetchNotifs = async function() {
      try {
        var res = await getNotifications();
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount || 0);
      } catch (err) {}
    };
    fetchNotifs();
    var interval = setInterval(fetchNotifs, 60000);
    return function() { clearInterval(interval); };
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await getHealingRequests();
      setRequests(res.data.data);
    } catch (error) {
      console.log('Error:', error);
    } finally {
      setLoading(false);
    }
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consentComplementary || !form.consentLiability || !form.consentTestimonial) {
      alert('Please agree to all three consent statements before submitting.');
      return;
    }
    try {
      await createHealingRequest({ ...form, isForSelf: form.isForSelf === true || form.isForSelf === 'true' });
      alert('Healing request submitted. Admin will assign a healer shortly.');
      setShowForm(false);
      setForm({
        condition: '', description: '', urgency: 'normal',
        isForSelf: true, beneficiaryName: '', beneficiaryAge: '',
        beneficiaryRelationship: 'spouse', beneficiaryRelationshipOther: '',
        medicalReportsShared: false, medicalReportsNote: '',
        consentComplementary: false, consentLiability: false, consentTestimonial: false
      });
      fetchRequests();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error submitting request');
    }
  };

  var handleSubmitFeedback = async function(requestId, sessionIndex) {
    if (!feedbackForm.text) {
      alert('Please enter your feedback.');
      return;
    }
    try {
      await submitSessionFeedback(requestId, sessionIndex, { feedbackText: feedbackForm.text });
      alert('Thank you! Your feedback has been submitted.');
      setFeedbackForm({ requestId: null, sessionIndex: null, text: '' });
      fetchRequests();
    } catch (error) {
      alert(error.response ? error.response.data.message : 'Error submitting feedback');
    }
  };
 
  const activeRequests = requests.filter(function(r) { return r.status === 'assigned' || r.status === 'in_progress'; });
  const pendingRequests = requests.filter(function(r) { return r.status === 'pending'; });
  const completedRequests = requests.filter(function(r) { return r.status === 'completed'; });
  const totalSessions = requests.reduce(function(sum, r) { return sum + (r.totalSessionsCompleted || 0); }, 0);
 
  const statusColors = {
    pending: { bg: '#fef9e7', color: '#d4ac0d' },
    assigned: { bg: '#eaf4fb', color: '#2980b9' },
    in_progress: { bg: '#eafaf1', color: '#27ae60' },
    completed: { bg: '#f0f0f0', color: '#666' },
    cancelled: { bg: '#fdf0f0', color: '#e74c3c' }
  };
 
  const urgencyColors = {
    normal: { bg: '#eafaf1', color: '#27ae60' },
    urgent: { bg: '#fef9e7', color: '#d4ac0d' },
    critical: { bg: '#fdf0f0', color: '#e74c3c' }
  };
 
  const whatsappReportUrl = (process.env.REACT_APP_CENTRE_WHATSAPP_URL || '') + '?text=Hello%2C%20I%20would%20like%20to%20share%20my%20medical%20reports%20for%20my%20healing%20request.%20My%20name%20is%20' + encodeURIComponent((user && user.firstName) || '') + '.';
 
  var s = {
    topbarInner: { ...styles.topbarInner, padding: isMobile ? '12px 16px' : '0 32px', height: isMobile ? 'auto' : '64px', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? '8px' : '0' },
    container: { ...styles.container, padding: isMobile ? '0 16px' : '0 32px' },
    tabs: { ...styles.tabs, overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch', width: '100%' },
    welcomeBanner: { ...styles.welcomeBanner, flexDirection: isMobile ? 'column' : 'row', padding: isMobile ? '20px 16px' : '28px 32px', alignItems: isMobile ? 'flex-start' : 'center' },
    welcomeStats: { ...styles.welcomeStats, flexWrap: 'wrap', gap: isMobile ? '12px' : '0', width: isMobile ? '100%' : 'auto', padding: isMobile ? '12px 16px' : '16px 24px' },
    welcomeStatDivider: { ...styles.welcomeStatDivider, display: isMobile ? 'none' : 'block' },
    homeGrid: { ...styles.homeGrid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)' },
    formGrid: { ...styles.formGrid, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' },
    formGrid3: { ...styles.formGrid3, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr' },
    formBtns: { ...styles.formBtns, flexDirection: isMobile ? 'column' : 'row' },
    submitBtn: { ...styles.submitBtn, width: isMobile ? '100%' : 'auto' },
    cancelBtn: { ...styles.cancelBtn, width: isMobile ? '100%' : 'auto' },
    requestInfoRow: { ...styles.requestInfoRow, flexDirection: isMobile ? 'column' : 'row' },
    historySession: { ...styles.historySession, gridTemplateColumns: isMobile ? '1fr' : '160px 1fr auto' },
    reportsHeader: { ...styles.reportsHeader, flexDirection: isMobile ? 'column' : 'row' }
  };

  const renderForm = function() {
    return (
      <div style={styles.formBox}>
        <div style={styles.formTitle}>New Healing Request</div>
        <form onSubmit={handleSubmit}>
 
          <div style={styles.toggleRow}>
            <div style={styles.toggleLabel}>Who is this request for?</div>
            <div style={styles.toggleBtns}>
              <button type="button" onClick={function() { setForm({ ...form, isForSelf: true }); }} style={form.isForSelf ? styles.toggleBtnActive : styles.toggleBtn}>Myself</button>
              <button type="button" onClick={function() { setForm({ ...form, isForSelf: false }); }} style={!form.isForSelf ? styles.toggleBtnActive : styles.toggleBtn}>Someone Else</button>
            </div>
          </div>
 
          {!form.isForSelf && (
            <div style={styles.beneficiaryBox}>
              <div style={styles.beneficiaryTitle}>Beneficiary Details</div>
              <div style={s.formGrid3}>
                <div style={styles.field}>
                  <label style={styles.label}>Patient Name</label>
                  <input style={styles.input} value={form.beneficiaryName} onChange={function(e) { setForm({ ...form, beneficiaryName: e.target.value }); }} placeholder="Full name of patient" required={!form.isForSelf} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Age</label>
                  <input style={styles.input} type="number" value={form.beneficiaryAge} onChange={function(e) { setForm({ ...form, beneficiaryAge: e.target.value }); }} placeholder="Age" min="1" max="120" required={!form.isForSelf} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Your Relationship</label>
                  <select style={styles.input} value={form.beneficiaryRelationship} onChange={function(e) { setForm({ ...form, beneficiaryRelationship: e.target.value }); }}>
                    <option value="spouse">Spouse</option>
                    <option value="child">Child</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="friend">Friend</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {form.beneficiaryRelationship === 'other' && (
                  <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Specify Relationship</label>
                    <input style={styles.input} value={form.beneficiaryRelationshipOther} onChange={function(e) { setForm({ ...form, beneficiaryRelationshipOther: e.target.value }); }} placeholder="e.g. Colleague, Neighbour..." />
                  </div>
                )}
              </div>
            </div>
          )}
 
          <div style={s.formGrid}>
            <div style={styles.field}>
              <label style={styles.label}>Condition / Health Concern</label>
              <input style={styles.input} value={form.condition} onChange={function(e) { setForm({ ...form, condition: e.target.value }); }} placeholder="e.g. Diabetes, Back Pain, Stress" required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Urgency</label>
              <select style={styles.input} value={form.urgency} onChange={function(e) { setForm({ ...form, urgency: e.target.value }); }}>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
              <label style={styles.label}>Detailed Description</label>
              <textarea style={{ ...styles.input, height: '100px', resize: 'vertical' }} value={form.description} onChange={function(e) { setForm({ ...form, description: e.target.value }); }} placeholder="Describe in detail — duration, symptoms, previous treatments, medical history..." required />
            </div>
          </div>
 
          <div style={styles.reportsBox}>
            <div style={s.reportsHeader}>
              <div>
                <div style={styles.reportsTitle}>Medical Reports</div>
                <div style={styles.reportsSub}>Share any medical reports, test results or doctor notes via WhatsApp to help our healers understand your condition better.</div>
              </div>
              <a href={whatsappReportUrl} target="_blank" rel="noreferrer" style={styles.whatsappBtn}>Share via WhatsApp</a>
            </div>
            <div style={styles.checkRow}>
              <input type="checkbox" id="reportsShared" checked={form.medicalReportsShared} onChange={function(e) { setForm({ ...form, medicalReportsShared: e.target.checked }); }} style={{ marginRight: '8px', accentColor: '#4a8f82' }} />
              <label htmlFor="reportsShared" style={{ fontSize: '13px', color: '#333', cursor: 'pointer' }}>I have shared / will share my medical reports via WhatsApp</label>
            </div>
            {form.medicalReportsShared && (
              <div style={{ ...styles.field, marginTop: '12px' }}>
                <label style={styles.label}>Note about reports (optional)</label>
                <input style={styles.input} value={form.medicalReportsNote} onChange={function(e) { setForm({ ...form, medicalReportsNote: e.target.value }); }} placeholder="e.g. Blood reports from Jan 2026, MRI scan shared" />
              </div>
            )}
          </div>
 
          {/* Consent Section */}
          <div style={styles.consentBox}>
            <div style={styles.consentTitle}>Consent Declaration</div>
            <div style={styles.consentSub}>Please read and agree to all three statements before submitting your request.</div>
 
            <div style={styles.consentItem}>
              <input type="checkbox" id="consent1"
                checked={form.consentComplementary}
                onChange={function(e) { setForm({ ...form, consentComplementary: e.target.checked }); }}
                style={{ marginRight: '12px', accentColor: '#4a8f82', flexShrink: 0, marginTop: '2px' }}
              />
              <label htmlFor="consent1" style={styles.consentLabel}>
                I understand that Pranic Healing is not meant to replace conventional medicine but rather to complement it.
              </label>
            </div>
 
            <div style={styles.consentItem}>
              <input type="checkbox" id="consent2"
                checked={form.consentLiability}
                onChange={function(e) { setForm({ ...form, consentLiability: e.target.checked }); }}
                style={{ marginRight: '12px', accentColor: '#4a8f82', flexShrink: 0, marginTop: '2px' }}
              />
              <label htmlFor="consent2" style={styles.consentLabel}>
                I hereby release the person or organisations providing Pranic Healing from any liabilities as a result of the service received by me.
              </label>
            </div>
 
            <div style={{ ...styles.consentItem, borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
              <input type="checkbox" id="consent3"
                checked={form.consentTestimonial}
                onChange={function(e) { setForm({ ...form, consentTestimonial: e.target.checked }); }}
                style={{ marginRight: '12px', accentColor: '#4a8f82', flexShrink: 0, marginTop: '2px' }}
              />
              <label htmlFor="consent3" style={styles.consentLabel}>
                I hereby give my consent to the healer to use this testimonial for his Pranic Healing Certification Program.
              </label>
            </div>
          </div>
 
          <div style={s.formBtns}>
            <button type="submit" style={s.submitBtn}>Submit Healing Request</button>
            <button type="button" onClick={function() { setShowForm(false); }} style={s.cancelBtn}>Cancel</button>
          </div>
        </form>
      </div>
    );
  };
 
  return (
    <div style={styles.layout}>
      <div style={styles.topbar}>
        <div style={s.topbarInner}>
          <img src={process.env.REACT_APP_CENTRE_LOGO_URL} alt={process.env.REACT_APP_CENTRE_NAME} style={styles.logo} />
          <div style={styles.topbarRight}>
            <div style={styles.userInfo}>
              <span style={styles.userName}>Welcome, {user && user.firstName}</span>
              <span style={styles.userRole}>Patient Member</span>
            </div>
            <div style={{ position: 'relative' }}>
              <button onClick={function() {
                setShowNotifPanel(!showNotifPanel);
                if (!showNotifPanel && unreadCount > 0) {
                  markNotificationsRead().then(function() { setUnreadCount(0); setNotifications(function(prev) { return prev.map(function(n) { return Object.assign({}, n, { read: true }); }); }); }).catch(function() {});
                }
              }} style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '4px', color: 'rgba(255,255,255,0.6)', fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '18px' }}>🔔</span>
                {unreadCount > 0 && (
                  <span style={{ position: 'absolute', top: '-2px', right: '-4px', background: '#e74c3c', color: '#fff', borderRadius: '10px', padding: '1px 5px', fontSize: '9px', fontWeight: '700', minWidth: '14px', textAlign: 'center', lineHeight: '14px' }}>{unreadCount}</span>
                )}
              </button>
              {showNotifPanel && (
                <div style={{ position: 'absolute', top: '40px', right: 0, width: '300px', maxHeight: '360px', overflowY: 'auto', background: '#fff', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 1000, border: '1px solid #e0e0e0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #e0e0e0' }}>
                    <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666' }}>Notifications</span>
                    <button onClick={function() { setShowNotifPanel(false); }} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                  </div>
                  <div>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '12px' }}>No notifications</div>
                    ) : notifications.slice(0, 20).map(function(n, i) {
                      return (
                        <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0', background: n.read ? '#fff' : '#f9faf7' }}>
                          <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.5' }}>{n.message}</div>
                          <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>{new Date(n.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <button onClick={logout} style={styles.logoutBtn}>Logout</button>
          </div>
        </div>
      </div>
 
      <div style={styles.main}>
        <div style={s.container}>
 
          <div style={s.tabs}>
            {[
              { key: 'home', label: 'Dashboard' },
              { key: 'requests', label: 'My Requests (' + requests.length + ')' },
              { key: 'activities', label: 'My Activities' + (function() { var c = requests.reduce(function(n, r) { return n + (r.activities ? r.activities.filter(function(a) { return a.isActive; }).length : 0); }, 0); return c > 0 ? ' (' + c + ')' : ''; })() },
              { key: 'history', label: 'Healing History' },
              { key: 'profile', label: 'My Profile' }
            ].concat(
              (function() { var c = requests.reduce(function(n, r) { return n + (r.sessions || []).reduce(function(m, s) { return m + (s.feedbackRequests || []).filter(function(fr) { return fr.status === 'pending'; }).length; }, 0); }, 0); return c > 0 ? [{ key: 'feedback', label: 'Feedback (' + c + ')' }] : []; })()
            ).map(function(tab) {
              return (
                <button key={tab.key} onClick={function() {
                    if (tab.key === 'profile') { navigate('/patient/profile'); return; }
                    setActiveTab(tab.key); setShowForm(false);
                  }}
                  style={activeTab === tab.key ? { ...styles.tab, ...styles.tabActive } : styles.tab}>
                  {tab.label}
                </button>
              );
            })}
          </div>
 
          {loading ? (
            <div style={styles.loading}>Loading your dashboard...</div>
          ) : (
            <div>
 
              {/* Feedback Alert Banner — shows on all tabs */}
              {(function() { var c = requests.reduce(function(n, r) { return n + (r.sessions || []).reduce(function(m, s) { return m + (s.feedbackRequests || []).filter(function(fr) { return fr.status === 'pending'; }).length; }, 0); }, 0); return c > 0 && activeTab !== 'feedback' ? (
                <div style={{ background: '#eaf4fb', border: '1px solid #2980b9', borderRadius: '8px', padding: '14px 20px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#2980b9', marginBottom: '2px' }}>
                      Feedback Requested ({c})
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Your healer would love to hear about your healing experience.</div>
                  </div>
                  <button onClick={function() { setActiveTab('feedback'); }}
                    style={{ background: '#2980b9', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 20px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer', flexShrink: 0 }}>
                    Submit Feedback
                  </button>
                </div>
              ) : null; })()}

              {activeTab === 'home' && (
                <div>
                  <div style={s.welcomeBanner}>
                    <div style={styles.welcomeLeft}>
                      <div style={styles.welcomeGreeting}>Hello, {user && user.firstName}!</div>
                      <div style={styles.welcomeText}>Track your healing requests, view your assigned healer and monitor your progress.</div>
                      <div style={styles.welcomeBtns}>
                        <button onClick={function() { setActiveTab('requests'); setShowForm(true); }} style={styles.newRequestBtn}>+ New Healing Request</button>
                        <a href={whatsappReportUrl} target="_blank" rel="noreferrer" style={styles.whatsappBannerBtn}>Share Medical Reports</a>
                      </div>
                    </div>
                    <div style={s.welcomeStats}>
                      <div style={styles.welcomeStat}>
                        <div style={styles.welcomeStatNum}>{requests.length}</div>
                        <div style={styles.welcomeStatLabel}>Total Requests</div>
                      </div>
                      <div style={s.welcomeStatDivider}></div>
                      <div style={styles.welcomeStat}>
                        <div style={{ ...styles.welcomeStatNum, color: '#27ae60' }}>{activeRequests.length}</div>
                        <div style={styles.welcomeStatLabel}>Active</div>
                      </div>
                      <div style={s.welcomeStatDivider}></div>
                      <div style={styles.welcomeStat}>
                        <div style={{ ...styles.welcomeStatNum, color: '#2980b9' }}>{totalSessions}</div>
                        <div style={styles.welcomeStatLabel}>Sessions Done</div>
                      </div>
                      <div style={s.welcomeStatDivider}></div>
                      <div style={styles.welcomeStat}>
                        <div style={{ ...styles.welcomeStatNum, color: '#666' }}>{completedRequests.length}</div>
                        <div style={styles.welcomeStatLabel}>Completed</div>
                      </div>
                    </div>
                  </div>
 
                  <div style={s.homeGrid}>
                    <div style={{ ...styles.card, gridColumn: isMobile ? '1 / -1' : '1 / 3' }}>
                      <div style={styles.cardHeader}>
                        <div>
                          <div style={styles.cardTitle}>Active Healing Requests</div>
                          <div style={styles.cardSub}>Your current healing sessions</div>
                        </div>
                        <button onClick={function() { setActiveTab('requests'); setShowForm(true); }} style={styles.addBtn}>+ New Request</button>
                      </div>
                      <div style={styles.cardBody}>
                        {activeRequests.length === 0 && pendingRequests.length === 0 ? (
                          <div style={styles.empty}>
                            <div style={styles.emptyTitle}>No active requests</div>
                            <div style={styles.emptySub}>Submit a healing request to get started.</div>
                            <button onClick={function() { setActiveTab('requests'); setShowForm(true); }} style={styles.emptyBtn}>Submit Healing Request</button>
                          </div>
                        ) : [...activeRequests, ...pendingRequests].map(function(req) {
                          return (
                            <div key={req._id} style={styles.requestItem}>
                              <div style={styles.requestLeft}>
                                <div style={styles.requestCondition}>
                                  {req.condition}
                                  {!req.isForSelf && req.beneficiaryName && <span style={styles.beneficiaryTag}>for {req.beneficiaryName}</span>}
                                </div>
                                <div style={styles.requestDesc}>{req.description && req.description.substring(0, 100)}...</div>
                                <div style={styles.requestMeta}>Requested: {new Date(req.requestedAt).toLocaleDateString('en-IN')} | Sessions: {req.totalSessionsCompleted}</div>
                                {req.razorpayShortUrl && req.paymentStatus !== 'paid' && (
                                  <a href={req.razorpayShortUrl} target="_blank" rel="noopener noreferrer" style={styles.payLinkBtn}>Complete payment →</a>
                                )}
                              </div>
                              <div style={styles.requestRight}>
                                <div style={styles.healerInfo}>
                                  {req.assignedHealer ? (
                                    <div>
                                      <div style={styles.healerLabel}>Assigned Healer</div>
                                      <div style={styles.healerName}>{req.assignedHealer.firstName} {req.assignedHealer.lastName}</div>
                                    </div>
                                  ) : (
                                    <div>
                                      <div style={styles.healerLabel}>Assigned Healer</div>
                                      <div style={{ fontSize: '13px', color: '#d4ac0d', fontWeight: '600' }}>Pending</div>
                                    </div>
                                  )}
                                </div>
                                <div style={styles.requestBadges}>
                                  <span style={{ ...styles.badge, background: urgencyColors[req.urgency] ? urgencyColors[req.urgency].bg : '#f0f0f0', color: urgencyColors[req.urgency] ? urgencyColors[req.urgency].color : '#666' }}>{req.urgency}</span>
                                  <span style={{ ...styles.badge, background: statusColors[req.status] ? statusColors[req.status].bg : '#f0f0f0', color: statusColors[req.status] ? statusColors[req.status].color : '#666' }}>{req.status ? req.status.replace('_', ' ') : ''}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
 
                    <div style={styles.card}>
                      <div style={styles.cardHeader}><div style={styles.cardTitle}>Quick Actions</div></div>
                      <div style={{ padding: '16px' }}>
                        {[
                          { label: 'New Healing Request', desc: 'For yourself or someone else', action: function() { setActiveTab('requests'); setShowForm(true); }, color: '#4a8f82' },
                          { label: 'Share Medical Reports', desc: 'Send reports via WhatsApp', action: function() { window.open(whatsappReportUrl, '_blank'); }, color: '#25D366' },
                          { label: 'Healing History', desc: 'View all past sessions', action: function() { setActiveTab('history'); }, color: '#2980b9' },
                          { label: 'Contact Centre', desc: 'WhatsApp us for queries', action: function() { window.open(process.env.REACT_APP_CENTRE_WHATSAPP_URL, '_blank'); }, color: '#5a9a6e' }
                        ].map(function(action) {
                          return (
                            <button key={action.label} onClick={action.action} style={styles.quickAction}>
                              <div style={{ ...styles.quickActionDot, background: action.color }}></div>
                              <div>
                                <div style={styles.quickActionLabel}>{action.label}</div>
                                <div style={styles.quickActionDesc}>{action.desc}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
 
              {activeTab === 'requests' && (
                <div style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div>
                      <div style={styles.cardTitle}>All Healing Requests</div>
                      <div style={styles.cardSub}>Submit for yourself or on behalf of others</div>
                    </div>
                    <button onClick={function() { setShowForm(!showForm); }} style={styles.addBtn}>{showForm ? 'Cancel' : '+ New Request'}</button>
                  </div>
                  {showForm && renderForm()}
                  <div style={styles.cardBody}>
                    {requests.length === 0 ? (
                      <div style={styles.empty}>
                        <div style={styles.emptyTitle}>No requests yet</div>
                        <div style={styles.emptySub}>Click New Request to get started.</div>
                      </div>
                    ) : requests.map(function(req) {
                      return (
                        <div key={req._id} style={styles.requestFull}>
                          <div style={styles.requestFullHeader}>
                            <div>
                              <div style={styles.requestCondition}>
                                {req.condition}
                                {!req.isForSelf && req.beneficiaryName && <span style={styles.beneficiaryTag}>for {req.beneficiaryName} ({req.beneficiaryRelationship})</span>}
                              </div>
                              <div style={styles.requestMeta}>
                                {new Date(req.requestedAt).toLocaleDateString('en-IN')}
                                {req.medicalReportsShared && <span style={{ marginLeft: '12px', color: '#25D366', fontSize: '11px', fontWeight: '700' }}>Reports Shared</span>}
                              </div>
                            </div>
                            <div style={styles.requestBadges}>
                              <span style={{ ...styles.badge, background: urgencyColors[req.urgency] ? urgencyColors[req.urgency].bg : '#f0f0f0', color: urgencyColors[req.urgency] ? urgencyColors[req.urgency].color : '#666' }}>{req.urgency}</span>
                              <span style={{ ...styles.badge, background: statusColors[req.status] ? statusColors[req.status].bg : '#f0f0f0', color: statusColors[req.status] ? statusColors[req.status].color : '#666' }}>{req.status ? req.status.replace('_', ' ') : ''}</span>
                            </div>
                          </div>
                          <div style={styles.requestDesc}>{req.description}</div>
                          {req.razorpayShortUrl && req.paymentStatus !== 'paid' && (
                            <a href={req.razorpayShortUrl} target="_blank" rel="noopener noreferrer" style={styles.payLinkBtn}>Complete payment →</a>
                          )}
                          <div style={s.requestInfoRow}>
                            <div style={styles.requestInfoBox}>
                              <div style={styles.requestInfoLabel}>Assigned Healer</div>
                              <div style={styles.requestInfoValue}>{req.assignedHealer ? req.assignedHealer.firstName + ' ' + req.assignedHealer.lastName : 'Pending'}</div>
                            </div>
                            <div style={styles.requestInfoBox}>
                              <div style={styles.requestInfoLabel}>Sessions Completed</div>
                              <div style={styles.requestInfoValue}>{req.totalSessionsCompleted}</div>
                            </div>
                            {req.promoApplied && req.promoApplied.code && (
                              <div style={styles.requestInfoBox}>
                                <div style={styles.requestInfoLabel}>Promo Applied</div>
                                <div style={{ ...styles.requestInfoValue, color: '#27ae60', fontWeight: '700' }}>
                                  {req.promoApplied.code} · ₹{req.promoApplied.discountAmount.toLocaleString('en-IN')} off
                                </div>
                              </div>
                            )}
                            {!req.isForSelf && req.beneficiaryName && (
                              <div style={styles.requestInfoBox}>
                                <div style={styles.requestInfoLabel}>Patient</div>
                                <div style={styles.requestInfoValue}>{req.beneficiaryName}, Age {req.beneficiaryAge}</div>
                              </div>
                            )}
                          </div>
                          <button onClick={function() { setExpandedRequest(expandedRequest === req._id ? null : req._id); }} style={styles.expandBtn}>
                            {expandedRequest === req._id ? 'Hide History' : 'View Session History (' + (req.sessions ? req.sessions.length : 0) + ')'}
                          </button>
                          {expandedRequest === req._id && req.sessions && req.sessions.length > 0 && (
                            <div style={styles.sessionHistory}>
                              {req.sessions.map(function(session, index) {
                                return (
                                  <div key={index} style={styles.sessionHistoryItem}>
                                    <div style={styles.sessionHistoryDate}>Session #{index + 1} — {new Date(session.date).toLocaleDateString('en-IN')}</div>
                                    <div style={styles.sessionHistoryRemarks}>{session.session_notes_patient || 'No remarks'}</div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Per-session feedback requests */}
                          {(req.sessions || []).map(function(session, sIdx) {
                            var pending = (session.feedbackRequests || []).find(function(fr) { return fr.status === 'pending'; });
                            var sessionDateStr = new Date(session.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                            if (!pending) return null;
                            return (
                              <div key={sIdx} style={{ marginTop: '12px', background: '#eaf4fb', border: '1px solid #2980b9', borderRadius: '8px', padding: '16px' }}>
                                <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#2980b9', marginBottom: '4px' }}>Feedback Requested — Session on {sessionDateStr}</div>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>Please share your experience for this session ({req.condition}).</div>
                                {feedbackForm.requestId === req._id && feedbackForm.sessionIndex === sIdx ? (
                                  <div>
                                    <textarea style={{ width: '100%', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', height: '80px', resize: 'vertical' }}
                                      value={feedbackForm.text}
                                      onChange={function(e) { setFeedbackForm(function(prev) { return Object.assign({}, prev, { text: e.target.value }); }); }}
                                      placeholder="Share your healing experience, how you feel, any improvements..." />
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                      <button onClick={function() { handleSubmitFeedback(req._id, sIdx); }}
                                        style={{ background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '10px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer' }}>
                                        Submit Feedback
                                      </button>
                                      <button onClick={function() { setFeedbackForm({ requestId: null, sessionIndex: null, text: '' }); }}
                                        style={{ background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '10px 20px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button onClick={function() { setFeedbackForm({ requestId: req._id, sessionIndex: sIdx, text: '' }); }}
                                    style={{ background: '#2980b9', color: '#fff', border: 'none', borderRadius: '4px', padding: '10px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer' }}>
                                    Write Feedback
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
 
              {activeTab === 'history' && (
                <div>
                  {requests.every(function(r) { return !r.sessions || r.sessions.length === 0; }) ? (
                    <div style={styles.card}>
                      <div style={{ padding: '48px', textAlign: 'center' }}>
                        <div style={styles.emptyTitle}>No healing history yet</div>
                        <div style={styles.emptySub}>Your session history will appear here after your first session.</div>
                      </div>
                    </div>
                  ) : requests.map(function(req) {
                    if (!req.sessions || req.sessions.length === 0) return null;
                    var healerName = req.assignedHealer ? req.assignedHealer.firstName + ' ' + req.assignedHealer.lastName : 'Unassigned';
                    var isComplete = req.status === 'completed';
                    var sessionsDone = req.sessions.filter(function(s) { return s.status === 'completed'; }).length;
                    var sessionsPlanned = req.sessions_planned || sessionsDone;
                    return (
                      <div key={req._id} style={{ marginBottom: '32px' }}>
                        {/* Case header */}
                        <div style={{ background: '#fff', borderRadius: '10px 10px 0 0', padding: '20px 24px', borderBottom: '2px solid #4a8f82', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                          <div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', fontFamily: "'Raleway', sans-serif", marginBottom: '4px' }}>
                              {req.condition}
                              {!req.isForSelf && req.beneficiaryName && <span style={styles.beneficiaryTag}>for {req.beneficiaryName}</span>}
                            </div>
                            <div style={{ fontSize: '12px', color: '#999' }}>
                              Healer: <strong style={{ color: '#4a8f82' }}>{healerName}</strong>
                              &nbsp;&nbsp;·&nbsp;&nbsp;Started: {new Date(req.requestedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              {isComplete && req.completedAt && <>&nbsp;&nbsp;·&nbsp;&nbsp;Completed: {new Date(req.completedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '20px', fontWeight: '700', color: '#4a8f82', fontFamily: "'Raleway', sans-serif" }}>{sessionsDone}{sessionsPlanned > sessionsDone ? <span style={{ fontSize: '13px', color: '#bbb' }}>/{sessionsPlanned}</span> : ''}</div>
                              <div style={{ fontSize: '10px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px' }}>Sessions</div>
                            </div>
                            <span style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '20px', fontWeight: '700', background: isComplete ? '#f0f0f0' : '#eafaf1', color: isComplete ? '#666' : '#27ae60' }}>
                              {isComplete ? 'Completed' : 'Active'}
                            </span>
                          </div>
                        </div>

                        {/* Progress bar */}
                        {sessionsPlanned > 0 && (
                          <div style={{ background: '#fff', padding: '12px 24px 0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span style={{ fontSize: '10px', color: '#999' }}>Healing Progress</span>
                              <span style={{ fontSize: '10px', fontWeight: '700', color: '#4a8f82' }}>{Math.round((sessionsDone / sessionsPlanned) * 100)}%</span>
                            </div>
                            <div style={{ height: '6px', background: '#f0f0f0', borderRadius: '4px', overflow: 'hidden', marginBottom: '16px' }}>
                              <div style={{ height: '100%', width: Math.min(100, Math.round((sessionsDone / sessionsPlanned) * 100)) + '%', background: isComplete ? '#27ae60' : '#4a8f82', borderRadius: '4px', transition: 'width 0.6s ease' }} />
                            </div>
                          </div>
                        )}

                        {/* Timeline */}
                        <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '8px 24px 24px', boxShadow: '0 2px 6px rgba(0,0,0,0.06)', position: 'relative' }}>
                          {req.sessions.map(function(session, index) {
                            var isLast = index === req.sessions.length - 1;
                            var isDone = session.status === 'completed';
                            return (
                              <div key={index} style={{ display: 'flex', gap: '0', position: 'relative', paddingBottom: isLast ? '0' : '0' }}>
                                {/* Timeline column */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '36px', flexShrink: 0, marginRight: '16px' }}>
                                  <div style={{ marginTop: '20px', width: '28px', height: '28px', borderRadius: '50%', background: isDone ? '#4a8f82' : '#f0f0f0', border: '3px solid ' + (isDone ? '#4a8f82' : '#ddd'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1, position: 'relative' }}>
                                    {isDone
                                      ? <span style={{ color: '#fff', fontSize: '11px', fontWeight: '700' }}>✓</span>
                                      : <span style={{ color: '#bbb', fontSize: '10px', fontWeight: '700' }}>{index + 1}</span>
                                    }
                                  </div>
                                  {!isLast && <div style={{ width: '2px', flex: 1, background: 'linear-gradient(180deg, #4a8f82 0%, #e0e0e0 100%)', minHeight: '24px' }} />}
                                </div>

                                {/* Card */}
                                <div style={{ flex: 1, paddingTop: '14px', paddingBottom: isLast ? '0' : '8px' }}>
                                  <div style={{ background: '#f9faf7', borderRadius: '8px', padding: '14px 16px', border: '1px solid #e8f0e8' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '6px', marginBottom: session.session_notes_patient ? '10px' : '0' }}>
                                      <div>
                                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a2e', fontFamily: "'Raleway', sans-serif" }}>
                                          Session {index + 1}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                                          {new Date(session.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                          &nbsp;·&nbsp;{session.duration || 60} min
                                        </div>
                                      </div>
                                      <span style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '12px', fontWeight: '700', background: isDone ? '#eafaf1' : '#fef9e7', color: isDone ? '#27ae60' : '#d4ac0d' }}>
                                        {session.status || 'completed'}
                                      </span>
                                    </div>
                                    {session.session_notes_patient && (
                                      <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.6', borderTop: '1px solid #e0e8e0', paddingTop: '10px' }}>
                                        {session.session_notes_patient}
                                      </div>
                                    )}
                                    {session.patientFeedback && (
                                      <div style={{ marginTop: '10px', background: '#f0f7f5', borderLeft: '3px solid #4a8f82', borderRadius: '4px', padding: '8px 12px' }}>
                                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#4a8f82', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Your Feedback</div>
                                        <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.5' }}>{session.patientFeedback}</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Completion badge */}
                          {isComplete && (
                            <div style={{ marginTop: '16px', background: 'linear-gradient(135deg, #eafaf1, #f0f7f5)', border: '1px solid #a9dfbf', borderRadius: '8px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#27ae60', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ color: '#fff', fontSize: '16px' }}>✓</span>
                              </div>
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a5c38', fontFamily: "'Raleway', sans-serif" }}>Healing Journey Complete</div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{sessionsDone} session{sessionsDone !== 1 ? 's' : ''} completed with {healerName}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
 
              {/* ACTIVITIES TAB */}
              {activeTab === 'activities' && (
                <div>
                  <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' }}>My Activities</div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>Daily practices assigned by your healer. Mark each one done as you complete it.</div>
                  {(function() {
                    var activeReqs = requests.filter(function(r) { return r.activities && r.activities.some(function(a) { return a.isActive; }); });
                    if (activeReqs.length === 0) {
                      return (
                        <div style={{ background: '#f9faf7', border: '1px dashed #ccc', borderRadius: '8px', padding: '32px', textAlign: 'center', color: '#999' }}>
                          No activities have been assigned yet. Your healer will assign activities as part of your healing programme.
                        </div>
                      );
                    }
                    return activeReqs.map(function(req) {
                      return (
                        <div key={req._id} style={{ marginBottom: '24px' }}>
                          <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '14px', color: '#4a8f82', marginBottom: '10px', paddingBottom: '6px', borderBottom: '1px solid #e8f0e8' }}>
                            {req.condition} {req.assignedHealer ? '— ' + req.assignedHealer.firstName + ' ' + req.assignedHealer.lastName : ''}
                          </div>
                          <PatientActivityCheckin
                            requestId={req._id}
                            activities={activitiesData[req._id] || req.activities || []}
                            onUpdate={function(updated) {
                              setActivitiesData(function(prev) { return Object.assign({}, prev, { [req._id]: updated }); });
                            }}
                          />
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              {/* FEEDBACK TAB */}
              {activeTab === 'feedback' && (function() {
                var pendingItems = [];
                requests.forEach(function(req) {
                  (req.sessions || []).forEach(function(session, sIdx) {
                    var pending = (session.feedbackRequests || []).find(function(fr) { return fr.status === 'pending'; });
                    if (pending) {
                      pendingItems.push({ req: req, session: session, sIdx: sIdx });
                    }
                  });
                });
                return (
                  <div>
                    {pendingItems.map(function(item) {
                      var sessionDateStr = new Date(item.session.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                      var healerName = item.req.assignedHealer ? item.req.assignedHealer.firstName + ' ' + item.req.assignedHealer.lastName : 'N/A';
                      return (
                        <div key={item.req._id + '_' + item.sIdx} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px', marginBottom: '16px' }}>
                          <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '2px' }}>{item.req.condition}</div>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>Session on {sessionDateStr} | Healer: {healerName}</div>
                          <div style={{ background: '#eaf4fb', border: '1px solid #2980b9', borderRadius: '8px', padding: '16px' }}>
                            <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#2980b9', marginBottom: '8px' }}>Share Your Healing Experience</div>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>Your feedback helps us improve and assists our healers in their certification journey.</div>
                            {feedbackForm.requestId === item.req._id && feedbackForm.sessionIndex === item.sIdx ? (
                              <div>
                                <textarea style={{ width: '100%', padding: '12px 14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', height: '100px', resize: 'vertical' }}
                                  value={feedbackForm.text}
                                  onChange={function(e) { setFeedbackForm(function(prev) { return Object.assign({}, prev, { text: e.target.value }); }); }}
                                  placeholder="How did the healing session help you? Any improvements you noticed? Share your experience..." />
                                <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                                  <button onClick={function() { handleSubmitFeedback(item.req._id, item.sIdx); }}
                                    style={{ background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '10px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer' }}>
                                    Submit Feedback
                                  </button>
                                  <button onClick={function() { setFeedbackForm({ requestId: null, sessionIndex: null, text: '' }); }}
                                    style={{ background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '10px 20px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button onClick={function() { setFeedbackForm({ requestId: item.req._id, sessionIndex: item.sIdx, text: '' }); }}
                                style={{ background: '#2980b9', color: '#fff', border: 'none', borderRadius: '4px', padding: '12px 28px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer' }}>
                                Write Your Feedback
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {pendingItems.length === 0 && (
                      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '48px', textAlign: 'center' }}>
                        <div style={{ fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' }}>No pending feedback requests</div>
                        <div style={{ fontSize: '13px', color: '#999' }}>All feedback has been submitted. Thank you!</div>
                      </div>
                    )}
                  </div>
                );
              })()}

            </div>
          )}
        </div>
      </div>
    </div>
  );
};
 
const styles = {
  layout: { minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
  topbar: { background: 'linear-gradient(135deg, #071e12, #0c2617)', borderBottom: '3px solid #4a8f82' },
  topbarInner: { maxWidth: '1100px', margin: '0 auto', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { height: '40px' },
  topbarRight: { display: 'flex', alignItems: 'center', gap: '20px' },
  userInfo: { textAlign: 'right' },
  userName: { display: 'block', fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#fff' },
  userRole: { fontSize: '10px', color: '#8DC63F', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '700' },
  logoutBtn: { padding: '8px 20px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' },
  main: { padding: '32px 0' },
  container: { maxWidth: '1100px', margin: '0 auto', padding: '0 32px' },
  tabs: { display: 'flex', gap: '4px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px', marginBottom: '24px', width: 'fit-content' },
  tab: { padding: '10px 20px', background: 'transparent', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#666', cursor: 'pointer' },
  tabActive: { background: '#4a8f82', color: '#fff' },
  loading: { textAlign: 'center', padding: '60px', color: '#666', fontSize: '14px' },
  welcomeBanner: { background: 'linear-gradient(135deg, #071e12, #0c2617)', borderRadius: '8px', padding: '28px 32px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '24px' },
  welcomeLeft: { flex: 1 },
  welcomeGreeting: { fontFamily: "'Raleway', sans-serif", fontSize: '22px', fontWeight: '700', color: '#fff', marginBottom: '8px' },
  welcomeText: { fontSize: '13px', color: 'rgba(255,255,255,0.55)', lineHeight: '1.7', maxWidth: '500px', marginBottom: '20px' },
  welcomeBtns: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  newRequestBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '10px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  whatsappBannerBtn: { display: 'inline-block', background: '#25D366', color: '#fff', padding: '10px 24px', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', textDecoration: 'none' },
  welcomeStats: { display: 'flex', alignItems: 'center', flexShrink: 0, background: 'rgba(255,255,255,0.06)', borderRadius: '8px', padding: '16px 24px' },
  welcomeStat: { textAlign: 'center', padding: '0 20px' },
  welcomeStatNum: { fontFamily: "'Raleway', sans-serif", fontSize: '28px', fontWeight: '700', color: '#8DC63F', lineHeight: 1 },
  welcomeStatLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' },
  welcomeStatDivider: { width: '1px', height: '40px', background: 'rgba(255,255,255,0.1)' },
  homeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
  cardHeader: { padding: '18px 24px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
  cardSub: { fontSize: '12px', color: '#999', marginTop: '2px' },
  cardBody: { padding: '8px 0' },
  addBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '9px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  empty: { padding: '32px', textAlign: 'center' },
  emptyTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' },
  emptySub: { fontSize: '13px', color: '#999', marginBottom: '16px' },
  emptyBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '9px 20px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' },
  formBox: { padding: '24px', borderBottom: '1px solid #e0e0e0', background: '#f9faf7' },
  formTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '20px' },
  toggleRow: { marginBottom: '20px' },
  toggleLabel: { fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '8px' },
  toggleBtns: { display: 'flex', gap: '8px' },
  toggleBtn: { padding: '9px 24px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#666', cursor: 'pointer' },
  toggleBtnActive: { padding: '9px 24px', background: '#4a8f82', border: '1px solid #4a8f82', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#fff', cursor: 'pointer' },
  beneficiaryBox: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', marginBottom: '20px' },
  beneficiaryTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#4a8f82', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
  formGrid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' },
  field: {},
  label: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666', marginBottom: '6px', fontFamily: "'Raleway', sans-serif" },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333' },
  reportsBox: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', marginBottom: '20px' },
  reportsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '16px' },
  reportsTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  reportsSub: { fontSize: '12px', color: '#666', lineHeight: '1.6' },
  whatsappBtn: { display: 'inline-block', background: '#25D366', color: '#fff', padding: '9px 18px', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', textDecoration: 'none', flexShrink: 0 },
  checkRow: { display: 'flex', alignItems: 'center' },
  consentBox: { background: '#fff', border: '2px solid #4a8f82', borderRadius: '8px', padding: '20px 24px', marginBottom: '20px' },
  consentTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '6px' },
  consentSub: { fontSize: '12px', color: '#666', marginBottom: '16px', lineHeight: '1.5' },
  consentItem: { display: 'flex', alignItems: 'flex-start', marginBottom: '14px', paddingBottom: '14px', borderBottom: '1px solid #f0f0f0' },
  consentLabel: { fontSize: '13px', color: '#333', lineHeight: '1.6', cursor: 'pointer' },
  formBtns: { display: 'flex', gap: '12px', marginTop: '8px' },
  submitBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '12px 28px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  cancelBtn: { background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px 24px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  requestItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 24px', borderBottom: '1px solid #f5f5f5', gap: '16px' },
  requestLeft: { flex: 1 },
  requestRight: { flexShrink: 0, textAlign: 'right' },
  requestCondition: { fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  beneficiaryTag: { marginLeft: '8px', fontSize: '11px', color: '#4a8f82', fontWeight: '600', fontStyle: 'italic' },
  requestDesc: { fontSize: '12px', color: '#666', lineHeight: '1.6', marginBottom: '6px' },
  requestMeta: { fontSize: '11px', color: '#999' },
  requestBadges: { display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '8px' },
  healerInfo: { marginBottom: '8px' },
  healerLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#999', marginBottom: '3px', fontFamily: "'Raleway', sans-serif" },
  healerName: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e' },
  requestFull: { padding: '20px 24px', borderBottom: '1px solid #f0f0f0' },
  requestFullHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  requestInfoRow: { display: 'flex', gap: '12px', marginTop: '12px', marginBottom: '12px' },
  requestInfoBox: { background: '#f9faf7', borderRadius: '6px', padding: '10px 14px', flex: 1 },
  requestInfoLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#999', marginBottom: '4px', fontFamily: "'Raleway', sans-serif" },
  requestInfoValue: { fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e' },
  expandBtn: { background: 'transparent', border: '1px solid #e0e0e0', color: '#4a8f82', borderRadius: '4px', padding: '7px 14px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' },
  sessionHistory: { marginTop: '12px', background: '#f9faf7', borderRadius: '6px', padding: '16px' },
  sessionHistoryItem: { paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #e0e0e0' },
  sessionHistoryDate: { fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  sessionHistoryRemarks: { fontSize: '13px', color: '#555', lineHeight: '1.6' },
  historyGroup: { borderBottom: '2px solid #f0f0f0' },
  historyGroupHeader: { padding: '16px 24px 8px', background: '#f9faf7' },
  historyCondition: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' },
  historyMeta: { fontSize: '12px', color: '#666' },
  historySession: { display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: '16px', alignItems: 'start', padding: '14px 24px', borderBottom: '1px solid #f5f5f5' },
  historySessionLeft: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  historySessionNum: { width: '24px', height: '24px', borderRadius: '50%', background: '#4a8f82', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', flexShrink: 0 },
  historySessionDate: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '2px' },
  historySessionDuration: { fontSize: '11px', color: '#999' },
  historySessionRemarks: { fontSize: '13px', color: '#555', lineHeight: '1.6' },
  badge: { fontFamily: "'Raleway', sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap' },
  quickAction: { display: 'flex', alignItems: 'flex-start', gap: '12px', width: '100%', background: 'none', border: 'none', padding: '12px', borderRadius: '6px', cursor: 'pointer', textAlign: 'left', marginBottom: '4px', borderBottom: '1px solid #f5f5f5' },
  quickActionDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, marginTop: '4px' },
  quickActionLabel: { fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', color: '#1a1a2e', marginBottom: '2px' },
  quickActionDesc: { fontSize: '11px', color: '#999' },
  payLinkBtn: { display: 'inline-block', marginTop: '8px', padding: '6px 14px', background: 'rgba(255,160,60,0.1)', border: '1px solid rgba(184,111,16,0.3)', borderRadius: '4px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', color: '#b86f10', textDecoration: 'none', letterSpacing: '0.5px' }
};
 
export default PatientDashboard;